import { zodResolver } from "@hookform/resolvers/zod";
import { T, useT } from "@transifex/react";
import { useNavigation } from "expo-router";
import { useCallback } from "react";
import { type Control, Controller, Form, type FormState, type UseFormClearErrors, useForm } from "react-hook-form";
import { Platform, Pressable } from "react-native";
import type { z } from "zod";

import ArrowLeft from "@/assets/icons/ArrowLeft";
import ArrowRight from "@/assets/icons/ArrowRight";
import Screen from "@/components/layout/Screen";
import AppButton from "@/components/shared/AppButton";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import PasswordInput from "@/components/shared/PasswordInput";
import { Box } from "@/components/ui/box";
import { FormControl, FormControlError, FormControlErrorText, FormControlLabel, FormControlLabelText } from "@/components/ui/form-control";
import { HStack } from "@/components/ui/hstack";
import { Input, InputField } from "@/components/ui/input";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthNavigation } from "@/hooks/useAuthNavigation";
import { useTimedEvent } from "@/hooks/useTimedEvent";
import { useToastNotify } from "@/stores/toastStore";
import { resetPasswordSchema } from "@/utils/validationSchemas";
import { useFocusEffect } from "@react-navigation/native";

type ResetPasswdSchemaType = z.infer<typeof resetPasswordSchema>;

interface ResetPasswordFormProps {
  control: Control<ResetPasswdSchemaType>;
  errors: FormState<ResetPasswdSchemaType>["errors"];
  clearErrors: UseFormClearErrors<ResetPasswdSchemaType>;
}

const ResetPasswordForm = ({
  control,
  errors,
  clearErrors,
  handleResendCode,
  handleSubmit,
  isLoading,
  resendEnabled,
}: ResetPasswordFormProps & { resendEnabled: boolean; handleResendCode: () => void; handleSubmit: () => Promise<void>; isLoading: boolean }) => {
  const t = useT();

  return (
    <Form control={control} onSubmit={handleSubmit}>
      <VStack className="gap-[20px]">
        <FormControl isDisabled={isLoading} isInvalid={!!errors.code} key="verification-code-input" className="gap-[10px]">
          <FormControlLabel className="flex flex-col items-start gap-1">
            <FormControlLabelText className="text-typography-900 text-[14px] leading-[20px] font-semibold align-middle">
              <T _str="Verification Code" />
            </FormControlLabelText>
            {resendEnabled && (
              <HStack className="justify-center items-center">
                <Text className="text-[14px] font-[400] leading-[20px] text-typography-900">
                  <T _str="Didn't receive the code? " />
                </Text>
                <Pressable className="active:opacity-70 inline" onPress={() => handleResendCode()}>
                  <Text className="text-[14px] font-[700] leading-[20px] underline text-typography-900">
                    <T _str="Resend Code." />
                  </Text>
                </Pressable>
              </HStack>
            )}
          </FormControlLabel>
          <Controller
            name="code"
            control={control}
            defaultValue=""
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                className={`bg-background-100 h-[52px] pl-[8px] pr-[16px] py-[8px] rounded-[6px] ${errors.code ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
              >
                <InputField
                  placeholder={t("Enter Your Verification Code")}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  onFocus={() => clearErrors("code")}
                  keyboardType="number-pad"
                  returnKeyType="done"
                  maxLength={6}
                  className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
                />
              </Input>
            )}
          />
          <FormControlError>
            <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{errors.code?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>

        <FormControl isDisabled={isLoading} isInvalid={!!errors.newPassword} key="new-password-input" className="gap-[10px]">
          <FormControlLabel>
            <FormControlLabelText className="text-typography-900 text-[14px] leading-[20px] font-semibold align-middle">
              <T _str="New Password" />
            </FormControlLabelText>
          </FormControlLabel>
          <Controller
            name="newPassword"
            control={control}
            defaultValue=""
            render={({ field: { onChange, onBlur, value } }) => (
              <PasswordInput
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                onFocus={() => clearErrors("newPassword")}
                error={errors.newPassword?.message}
              />
            )}
          />
          <FormControlError>
            <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{errors.newPassword?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>

        <FormControl isDisabled={isLoading} isInvalid={!!errors.confirmNewPassword} key="confirm-new-password-input" className="gap-[10px]">
          <FormControlLabel>
            <FormControlLabelText className="text-typography-900 text-[14px] leading-[20px] font-semibold align-middle">
              <T _str="Confirm New Password" />
            </FormControlLabelText>
          </FormControlLabel>
          <Controller
            name="confirmNewPassword"
            control={control}
            defaultValue=""
            render={({ field: { onChange, onBlur, value } }) => (
              <PasswordInput
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                onFocus={() => clearErrors("confirmNewPassword")}
                error={errors.confirmNewPassword?.message}
              />
            )}
          />
          <FormControlError>
            <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{errors.confirmNewPassword?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
      </VStack>
      {Platform.OS === "web" && <button className="hidden" type="submit" />}
    </Form>
  );
};

const ResetPassword = () => {
  const t = useT();
  const { pendingEmail, resetPassword } = useAuth();
  const { signIn, forgotPassword, isLoading } = useAuth();
  const { notify } = useToastNotify();
  const { enabled, reset: resetResend } = useTimedEvent(15000);
  const navigation = useNavigation();
  const handleAuthNavigation = useAuthNavigation();

  const {
    control,
    handleSubmit,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<ResetPasswdSchemaType>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      code: "",
      newPassword: "",
      confirmNewPassword: "",
    },
  });

  const onSubmit = useCallback(
    async (data: ResetPasswdSchemaType) => {
      if (!pendingEmail?.email && pendingEmail?.type !== "reset-password") {
        notify({ severity: "error", message: t("No email available to resend the code.") });
        return;
      }

      try {
        await resetPassword(pendingEmail.email, data.code, data.newPassword);
        await signIn(pendingEmail.email, data.newPassword);
        reset({
          code: "",
          newPassword: "",
          confirmNewPassword: "",
        });
        handleAuthNavigation();
      } catch (err) {
        console.error("Error reseting password:", err);
        notify({
          message: t("Invalid verification code."),
          severity: "error",
        });
      }
    },
    [resetPassword, pendingEmail, signIn, notify, t, handleAuthNavigation, reset],
  );

  const handleResendCode = async () => {
    if (!pendingEmail?.email && pendingEmail?.type !== "reset-password") {
      notify({ severity: "error", message: t("No email available to resend the code.") });
      return;
    }

    resetResend();
    await forgotPassword(pendingEmail.email);
  };

  const handleBack = () => {
    reset({ code: "", newPassword: "", confirmNewPassword: "" });
    navigation.goBack();
  };

  useFocusEffect(
    useCallback(() => {
      reset({ code: "", newPassword: "", confirmNewPassword: "" });
    }, [reset]),
  );

  return (
    <Screen>
      <VStack className="gap-[32px]">
        <PageTitle title={t("Reset Password")} />
        <Text className="text-typography-900 text-[14px] leading-[20px] font-[400] text-center">
          <T _str="If your account exists, you will receive an email with a verification code to enter below." />
        </Text>

        <ResetPasswordForm
          clearErrors={clearErrors}
          control={control}
          errors={errors}
          handleResendCode={handleResendCode}
          handleSubmit={handleSubmit(onSubmit)}
          isLoading={isLoading}
          resendEnabled={enabled}
        />
        <HStack className="w-full gap-3">
          <Box className="w-1/2">
            <AppButton onPress={handleBack} title={t("Back")} variant="outline" iconPosition="before" icon={ArrowLeft} />
          </Box>

          <Box className="w-1/2">
            <AppButton onPress={handleSubmit(onSubmit)} title={t("Submit")} variant="primary" icon={ArrowRight} iconPosition="after" loading={isLoading} />
          </Box>
        </HStack>
      </VStack>
    </Screen>
  );
};
export default ResetPassword;
